import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { notifyNewTodo, notifyTodoCompleted } from '../utils/notifications';
import './TodoList.css';

function TodoList({ user, userData }) {
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkTasks, setBulkTasks] = useState('');
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [multiAssign, setMultiAssign] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const isAdmin = userData?.role === 'admin';

  useEffect(() => {
    // Fetch todos
    const todosQuery = isAdmin
      ? query(collection(db, 'todos'), orderBy('createdAt', 'desc'))
      : query(
          collection(db, 'todos'),
          where('assignedTo', '==', user.uid),
          orderBy('createdAt', 'desc')
        );

    console.log('TodoList: Setting up query for user:', user.uid, 'isAdmin:', isAdmin);

    const unsubscribe = onSnapshot(todosQuery, (snapshot) => {
      const todosData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('TodoList: Received todos:', todosData.length, 'todos for user:', user.uid);
      console.log('TodoList: Todos data:', todosData);
      setTodos(todosData);
    }, (error) => {
      console.error('TodoList: Error fetching todos:', error);
    });

    return () => unsubscribe();
  }, [user.uid, isAdmin]);

  useEffect(() => {
    // Fetch users for admin
    if (isAdmin) {
      const usersQuery = query(collection(db, 'users'));
      const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
        const usersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsers(usersData);
      });

      return () => unsubscribe();
    }
  }, [isAdmin]);

  const handleAddTodo = async (e) => {
    e.preventDefault();
    if (!newTodo.trim()) return;
    
    // Check if we have assignees
    if (multiAssign) {
      if (selectedUsers.length === 0) {
        alert('Please select at least one person to assign');
        return;
      }
    } else {
      if (!assignedTo) return;
    }

    setLoading(true);
    const todoTitle = newTodo.trim();
    
    try {
      if (multiAssign) {
        // Create task for each selected user
        let successCount = 0;
        for (const userId of selectedUsers) {
          const docRef = await addDoc(collection(db, 'todos'), {
            title: todoTitle,
            assignedTo: userId,
            assignedBy: user.uid,
            assignedByName: userData?.name || user.email,
            completed: false,
            createdAt: serverTimestamp()
          });

          // Send notification
          notifyNewTodo(
            docRef.id,
            todoTitle,
            userId,
            user.uid,
            userData?.name || user.email
          ).catch(err => console.error('Failed to send notification:', err));

          successCount++;
        }
        alert(`✅ Task assigned to ${successCount} staff member${successCount > 1 ? 's' : ''}`);
        setSelectedUsers([]);
      } else {
        // Single assignment
        const docRef = await addDoc(collection(db, 'todos'), {
          title: todoTitle,
          assignedTo: assignedTo,
          assignedBy: user.uid,
          assignedByName: userData?.name || user.email,
          completed: false,
          createdAt: serverTimestamp()
        });

        // Send notification
        notifyNewTodo(
          docRef.id,
          todoTitle,
          assignedTo,
          user.uid,
          userData?.name || user.email
        ).catch(err => console.error('Failed to send notification:', err));
      }
      
      setNewTodo('');
      setAssignedTo('');
    } catch (error) {
      console.error('Error adding todo:', error);
      alert('Failed to add todo');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    setSelectedUsers(users.map(u => u.id));
  };

  const clearAllUsers = () => {
    setSelectedUsers([]);
  };

  const handleBulkAddTodos = async (e) => {
    e.preventDefault();
    if (!bulkTasks.trim() || !bulkAssignee) return;

    setLoading(true);
    
    // Split by newlines and filter empty lines
    const tasks = bulkTasks
      .split('\n')
      .map(task => task.trim())
      .filter(task => task.length > 0);

    if (tasks.length === 0) {
      alert('Please enter at least one task');
      setLoading(false);
      return;
    }

    try {
      let successCount = 0;
      
      // Add all tasks
      for (const task of tasks) {
        const docRef = await addDoc(collection(db, 'todos'), {
          title: task,
          assignedTo: bulkAssignee,
          assignedBy: user.uid,
          assignedByName: userData?.name || user.email,
          completed: false,
          createdAt: serverTimestamp()
        });

        // Send notification for each task
        notifyNewTodo(
          docRef.id,
          task,
          bulkAssignee,
          user.uid,
          userData?.name || user.email
        ).catch(err => console.error('Failed to send notification:', err));

        successCount++;
      }

      alert(`✅ Successfully added ${successCount} task${successCount > 1 ? 's' : ''}`);
      setBulkTasks('');
      setBulkAssignee('');
      setBulkMode(false);
    } catch (error) {
      console.error('Error adding bulk todos:', error);
      alert('Failed to add some tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTodo = async (todoId, currentStatus) => {
    // Find the todo to get its details
    const todo = todos.find(t => t.id === todoId);
    
    try {
      await updateDoc(doc(db, 'todos', todoId), {
        completed: !currentStatus,
        completedAt: !currentStatus ? serverTimestamp() : null
      });

      // Send notification if marking as complete
      if (!currentStatus && todo) {
        notifyTodoCompleted(
          todoId,
          todo.title,
          user.uid,
          userData?.name || user.email,
          todo.assignedBy
        ).catch(err => console.error('Failed to send notification:', err));
      }
    } catch (error) {
      console.error('Error updating todo:', error);
    }
  };

  const handleDeleteTodo = async (todoId) => {
    if (!window.confirm('Are you sure you want to delete this todo?')) return;

    try {
      await deleteDoc(doc(db, 'todos', todoId));
    } catch (error) {
      console.error('Error deleting todo:', error);
    }
  };

  const filteredTodos = todos.filter(todo => {
    if (filter === 'completed') return todo.completed;
    if (filter === 'pending') return !todo.completed;
    return true;
  });

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unknown User';
  };

  return (
    <div className="todo-container">
      <div className="todo-header">
        <h1>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          {isAdmin ? 'Manage Todos' : 'My Todos'}
        </h1>
        {/* Debug info - remove this later */}
        <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
          Debug: User ID: {user.uid}, Role: {userData?.role}, Is Admin: {isAdmin ? 'Yes' : 'No'}
        </div>
      </div>

      {isAdmin && (
        <div className="add-todo-card">
          <div className="todo-card-header">
            <h2>{bulkMode ? 'Add Multiple Tasks' : 'Create New Todo'}</h2>
            <button 
              className="toggle-mode-btn"
              onClick={() => setBulkMode(!bulkMode)}
              type="button"
            >
              {bulkMode ? '← Single Task' : 'Bulk Add →'}
            </button>
          </div>

          {!bulkMode ? (
            <form onSubmit={handleAddTodo} className="add-todo-form">
              <input
                type="text"
                value={newTodo}
                onChange={(e) => setNewTodo(e.target.value)}
                placeholder="Enter todo title..."
                className="todo-input"
                required
              />
              
              <div className="assign-mode-toggle">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={multiAssign}
                    onChange={(e) => {
                      setMultiAssign(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedUsers([]);
                      } else {
                        setAssignedTo('');
                      }
                    }}
                  />
                  <span>Assign to multiple staff</span>
                </label>
              </div>

              {!multiAssign ? (
                <select
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  className="user-select"
                  required
                >
                  <option value="">Assign to...</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.role})
                    </option>
                  ))}
                </select>
              ) : (
                <div className="multi-select-container">
                  <div className="multi-select-header">
                    <span className="selected-count">
                      {selectedUsers.length} selected
                    </span>
                    <div className="multi-select-actions">
                      <button type="button" onClick={selectAllUsers} className="select-action-btn">
                        Select All
                      </button>
                      <button type="button" onClick={clearAllUsers} className="select-action-btn">
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="user-checkboxes">
                    {users.map(u => (
                      <label key={u.id} className="user-checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(u.id)}
                          onChange={() => toggleUserSelection(u.id)}
                        />
                        <span className="user-checkbox-text">
                          {u.name} <span className="user-role">({u.role})</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <button type="submit" className="add-todo-btn" disabled={loading}>
                {loading ? 'Adding...' : multiAssign ? `Assign to ${selectedUsers.length} Staff` : 'Add Todo'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleBulkAddTodos} className="add-todo-form bulk-form">
              <textarea
                value={bulkTasks}
                onChange={(e) => setBulkTasks(e.target.value)}
                placeholder="Enter tasks (one per line)&#10;Example:&#10;Review project documentation&#10;Update team meeting notes&#10;Prepare monthly report"
                className="bulk-todo-input"
                rows="6"
                required
              />
              <div className="bulk-task-count">
                {bulkTasks.split('\n').filter(t => t.trim()).length} task(s)
              </div>
              <select
                value={bulkAssignee}
                onChange={(e) => setBulkAssignee(e.target.value)}
                className="user-select"
                required
              >
                <option value="">Assign all to...</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.role})
                  </option>
                ))}
              </select>
              <button type="submit" className="add-todo-btn bulk-add-btn" disabled={loading}>
                {loading ? 'Adding Tasks...' : 'Add All Tasks'}
              </button>
            </form>
          )}
        </div>
      )}

      <div className="todo-filters">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({todos.length})
        </button>
        <button
          className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Pending ({todos.filter(t => !t.completed).length})
        </button>
        <button
          className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
          onClick={() => setFilter('completed')}
        >
          Completed ({todos.filter(t => t.completed).length})
        </button>
      </div>

      <div className="todos-list">
        {filteredTodos.length === 0 ? (
          <div className="empty-todos">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p>No todos found</p>
            <span>{filter === 'completed' ? 'No completed tasks yet' : 'All caught up!'}</span>
          </div>
        ) : (
          filteredTodos.map(todo => (
            <div key={todo.id} className={`todo-item ${todo.completed ? 'completed' : ''}`}>
              <div className="todo-checkbox-wrapper">
                <input
                  type="checkbox"
                  checked={todo.completed}
                  onChange={() => handleToggleTodo(todo.id, todo.completed)}
                  className="todo-checkbox"
                />
              </div>
              <div className="todo-content">
                <h3 className="todo-title">{todo.title}</h3>
                <div className="todo-meta">
                  {isAdmin && (
                    <span className="todo-assigned">
                      Assigned to: {getUserName(todo.assignedTo)}
                    </span>
                  )}
                  <span className="todo-date">
                    {todo.createdAt?.toDate().toLocaleDateString()}
                  </span>
                  {todo.completed && todo.completedAt && (
                    <span className="todo-completed-date">
                      ✓ Completed {todo.completedAt.toDate().toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              {isAdmin && (
                <button
                  onClick={() => handleDeleteTodo(todo.id)}
                  className="delete-todo-btn"
                  title="Delete todo"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default TodoList;
